import { dayBudget, conflicts, unknownTravel, cleanParallel, spans } from './scheduleTimeline';
import { arrangeEntries } from './scheduleOperations';
import { isTime, toMin } from './scheduleTimes';
import { effectiveWorkingHours } from './scheduleWorkingHours';

// Mirrors backend/src/lib/schedule-warnings.js: Breakfast, Lunch and Dinner all count, a break
// named exactly "... Break" counts too, and it must be timed and long enough to be a real break.
const MEAL_TYPES = new Set(['BREAKFAST', 'LUNCH', 'DINNER']);
const MEAL_NAMES = /^(breakfast|lunch|dinner)(\s+(break|breakfast|lunch|dinner))?$/i;
const LUNCH_NAMES = /^lunch(\s+break)?$/i;
const isQualifyingMeal = e => e?.kind === 'activity' && (MEAL_TYPES.has(e.activityType) || MEAL_NAMES.test(String(e.name || '').trim())) && (e.durationMin || 0) > 0 && isTime(e.start);
const isLunch = e => e.activityType === 'LUNCH' || LUNCH_NAMES.test(String(e.name || '').trim());

// A day may run past midnight. Entries after midnight keep their clock time, so the day is read as the
// shortest continuous window that holds every entry: a Dinner Break at 00:30 then belongs to the day
// that began at 16:30 instead of looking like it happened before the day started.
export const mealTimeline = entries => {
  const list = spans(entries);
  if (list.length < 2) return list.map(s => ({ ...s })).sort((a, b) => a.from - b.from);
  let best = null;
  for (const c of list) {
    const shifted = list.map(s => (c.from - s.from > 0 ? { e: s.e, from: s.from + 1440, to: s.to + 1440 } : { e: s.e, from: s.from, to: s.to }));
    const span = Math.max(...shifted.map(s => s.to)) - Math.min(...shifted.map(s => s.from));
    if (!best || span < best.span) best = { span, shifted };
  }
  return best.shifted.sort((a, b) => a.from - b.from);
};



export const isDraftProtected = entry => Boolean(entry.locked || (entry.manualTime ?? isTime(entry.start)));
// Protected entries are returned exactly as submitted so server lock checks never see added fields.
export const retimeDraft = (entries, original = entries) => {
  const byId = new Map(entries.filter(isDraftProtected).map(e => [e.id, e]));
  return arrangeEntries(entries, true, original).map(e => byId.get(e.id) || e);
};
export const rearrangeDraft = (days, from, index, to, targetIndex) => {
  const source = days.find(d=>d.dayNumber===from); const target = days.find(d=>d.dayNumber===to);
  const entry = source?.entries[index];
  if (!entry || !target || isDraftProtected(entry)) return days;
  const next = days.map(d=>({...d,entries:[...d.entries]}));
  const a=next.find(d=>d.dayNumber===from), b=next.find(d=>d.dayNumber===to);
  if (!a || !b) return days;
  a.entries.splice(index,1); b.entries.splice(targetIndex,0,entry);
  // Frozen entries cannot be indirectly displaced in running order.
  if (days.some(d=>d.entries.some((e,i)=>isDraftProtected(e) && next.find(x=>x.dayNumber===d.dayNumber)?.entries[i]?.id!==e.id))) return days;
  const cleaned=next.map(d=>({...d,entries:cleanParallel(d.entries)}));
  if(days.some(d=>d.entries.some(e=>isDraftProtected(e)&&e.parallelGroupId!==cleaned.find(x=>x.dayNumber===d.dayNumber)?.entries.find(x=>x.id===e.id)?.parallelGroupId))) return days;
  return cleaned.map(d=>d.dayNumber===from || d.dayNumber===to ? {...d,entries:retimeDraft(d.entries, days.find(x=>x.dayNumber===d.dayNumber)?.entries)} : d);
};
export const draftBudget = entries => dayBudget(entries);
export const draftWarnings = (days, estimates=[], settings={}, timing={}) => {
  const warnings=[]; const allocations=new Map();const occurrences=new Map(); const latest=timing.mealLatest??360;
  for(const d of days) {
    if((d.entries||[]).some(e=>unknownTravel(e))) warnings.push({type:'UNKNOWN_TRAVEL',message:`Day ${d.dayNumber}: company-move travel time is not entered.`});
    const b=draftBudget(d.entries||[]); const hours=effectiveWorkingHours(d,settings);
    if(Number.isFinite(hours) && b.elapsed>hours*60) warnings.push({type:'OVERTIME',message:`Day ${d.dayNumber} needs ${b.elapsed} min but has ${hours*60} min.`});
    // Measured from the start of the day, through every qualifying break, to the end of the day.
    const timeline = mealTimeline(d.entries || []);
    if (timeline.length) {
      const end = Math.max(...timeline.map(s => s.to));
      const clock = min => `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
      const warn = (from, to) => warnings.push({type:'NO_MEAL_BREAK',intervalStartMin:from,intervalEndMin:to,
        message:`Day ${d.dayNumber}: More than ${latest/60} hours without a meal break: ${clock(from)}–${clock(to)}.`});
      let meal = timeline[0].from, reported = false;
      for (const s of timeline) {
        if (isQualifyingMeal(s.e)) {
          if (!reported && s.from-meal > latest) { warn(meal,s.from); reported = true; }
          if (isLunch(s.e) && s.from-meal < Math.min(180,timing.mealAfter??300)) warnings.push({type:'EARLY_MEAL',message:`Day ${d.dayNumber} has an early lunch.`});
          meal = Math.max(meal,s.to);
        }
      }
      if (!reported && end-meal > latest) warn(meal,end);
    }

    for(const c of conflicts(d.entries||[])) warnings.push({dayNumber:d.dayNumber,type:'CONFLICT',message:c.message});
    const scenes=(d.entries||[]).filter(e=>e.kind==='scene');
    if(scenes.length>8) warnings.push({type:'OVERLOADED',message:`Day ${d.dayNumber} has ${scenes.length} scene occurrences.`});
    for(const e of scenes) {allocations.set(e.sceneUid,(allocations.get(e.sceneUid)||0)+(e.durationMin||0));if(!occurrences.has(e.sceneUid)||!e.continuation)occurrences.set(e.sceneUid,{dayNumber:d.dayNumber,entryId:e.id});}
  }
  for(const e of estimates) if(Number.isInteger(e.minutes) && allocations.has(e.sceneUid) && allocations.get(e.sceneUid)<e.minutes) warnings.push({...occurrences.get(e.sceneUid),type:'UNDER_ALLOCATED',message:`Scene ${e.sceneUid}: ${allocations.get(e.sceneUid)} min across occurrences; estimate ${e.minutes} min.`});
  return warnings.map(w=>({...w,dayNumber:w.dayNumber??Number((/Day (\d+)/.exec(w.message)||[])[1])}));
};